import React from 'react';
import { alpha, Box, Paper, Stack, Typography } from '@mui/material';
import {
  LightbulbOutlined,
  ScienceOutlined,
  BarChartOutlined,
  PrecisionManufacturingOutlined,
  ArrowForward,
} from '@mui/icons-material';

const stages = [
  {
    id: 'idea',
    label: 'idea',
    number: 8,
    icon: <LightbulbOutlined />,
    color: '#006BFF',
  },
  {
    id: 'prototype',
    label: 'prototype',
    number: 5,
    icon: <ScienceOutlined />,
    color: '#148CFF',
    active: true,
  },
  {
    id: 'scale-up',
    label: 'scale-up',
    number: 2,
    icon: <BarChartOutlined />,
    color: '#FF2D2D',
  },
  {
    id: 'production',
    label: 'production',
    number: 1,
    icon: <PrecisionManufacturingOutlined />,
    color: '#43C936',
  },
];

function StageGateVisual() {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 260,
        p: 3,
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
          Stage-Gate Development
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mb: 3,
            lineHeight: 1.5,
          }}
        >
          Manage your formulation lifecycle from idea to production.
        </Typography>

        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          justifyContent="center"
          sx={{ width: '100%' }}
        >
          {stages.map((stage, index) => (
            <React.Fragment key={stage.id}>
              <Paper
                variant="outlined"
                sx={{
                  width: 108,
                  height: 150,
                  flexShrink: 0,
                  boxSizing: 'border-box',
                  p: 1.75,
                  textAlign: 'center',
                  borderRadius: 2,
                  borderColor: stage.active ? stage.color : 'divider',
                  borderWidth: stage.active ? 2 : 1,
                  bgcolor: stage.active
                    ? alpha(stage.color, 0.04)
                    : 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: '50%',
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: stage.color,
                    bgcolor: alpha(stage.color, 0.12),
                  }}
                >
                  {React.cloneElement(stage.icon, {
                    sx: { fontSize: 22 },
                  })}
                </Box>

                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 800,
                    color: stage.active ? stage.color : 'text.primary',
                    lineHeight: 1.2,
                    mb: 1.25,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {stage.label}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 800,
                    color: stage.active ? stage.color : 'text.primary',
                    lineHeight: 1.2,
                  }}
                >
                  {stage.number}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {stage.number === 1 ? 'formulation' : 'formulations'}
                </Typography>
              </Paper>

              {index < stages.length - 1 && (
                <ArrowForward
                  sx={{
                    color: 'text.secondary',
                    flexShrink: 0,
                    fontSize: 26,
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Box>
    </Box>
  );
}

export default StageGateVisual;